import {
  getAutocompleteDocumentQuery,
  getAutocompletePageQuery,
  getAutocompleteProductQuery,
  queryElasticSearchMulti
} from "../elasticSearch";
import { formatSuggestions } from "./search-formatter";
import type { FormatSuggestionsParams } from "./search-formatter";
import type { GetMicroCopy } from "../../components/MicroCopy";
import type { SuggestionGroup } from "@bmi-digital/components";

export const getAutocompleteSuggestions = async (
  term: string,
  getMicroCopy: GetMicroCopy,
  countryCode: string,
  variantCodeToPathMap?: Record<string, string>
): Promise<SuggestionGroup[]> => {
  const indexProducts = process.env.GATSBY_ES_INDEX_NAME_PRODUCTS;
  const indexPages = process.env.GATSBY_ES_INDEX_NAME_PAGES;
  const indexDocuments = process.env.GATSBY_ES_INDEX_NAME_DOCUMENTS;

  if (!indexProducts && !indexPages && !indexDocuments) {
    return [];
  }

  const queries: {
    indexName: string;
    query: Record<string, unknown>;
    type: "products" | "pages" | "documents";
  }[] = [];

  if (indexProducts) {
    queries.push({
      indexName: indexProducts,
      query: getAutocompleteProductQuery(term),
      type: "products"
    });
  }

  if (indexPages) {
    queries.push({
      indexName: indexPages,
      query: getAutocompletePageQuery(term),
      type: "pages"
    });
  }

  if (indexDocuments) {
    queries.push({
      indexName: indexDocuments,
      query: getAutocompleteDocumentQuery(term),
      type: "documents"
    });
  }

  if (queries.length === 0) {
    return [];
  }

  const multiResponse = (await queryElasticSearchMulti(queries).catch(
    () => undefined
  )) as { responses?: Array<{ error?: unknown }> } | undefined;

  const { products, pages, documents } = queries.reduce(
    (acc, q, index) => {
      // eslint-disable-next-line security/detect-object-injection
      const res = multiResponse?.responses?.[index];
      if (res && !res.error) {
        if (q.type === "products") {
          acc.products = res as FormatSuggestionsParams["products"];
        } else if (q.type === "pages") {
          acc.pages = res as FormatSuggestionsParams["pages"];
        } else if (q.type === "documents") {
          acc.documents = res as FormatSuggestionsParams["documents"];
        }
      }
      return acc;
    },
    {
      products: null as FormatSuggestionsParams["products"],
      pages: null as FormatSuggestionsParams["pages"],
      documents: null as FormatSuggestionsParams["documents"]
    }
  );

  const hasResults = [products, pages, documents].some(
    (res) => (res?.hits?.total?.value ?? 0) > 0
  );

  if (!hasResults) {
    return [];
  }

  return formatSuggestions({
    products,
    pages,
    documents,
    getMicroCopy,
    countryCode,
    term,
    variantCodeToPathMap
  });
};
